import PrpDemandRadarGapMap from "../components/prp/PrpDemandRadarGapMap";
import PrpPartnerConversionCockpit from "../components/prp/PrpPartnerConversionCockpit";
import {
  clearPrpUiContinuity,
  usePrpScrollRestoration,
  usePrpSessionState,
} from "../lib/prpUiContinuity";
import PrpTourneeTerrainIntelligente from "../components/prp/PrpTourneeTerrainIntelligente";
import PrpPipelineTerrainCockpit from "../components/prp/PrpPipelineTerrainCockpit";
import PrpNearbyScoutPanel from "../components/prp/PrpNearbyScoutPanel";
import PrpForfyAgentsPanel from "../components/prp/PrpForfyAgentsPanel";
import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import {
  FaBuilding, FaUsers, FaChartLine, FaMoneyBillWave,
  FaPlus, FaEdit, FaTrash, FaDownload, FaMedal, FaEnvelope,
  FaSync, FaSignOutAlt, FaArrowLeft, FaHome, FaLock, FaCheckCircle, FaTimes, FaLink, FaMapPin, FaClock, FaHistory, FaExclamationTriangle, FaCopy
} from 'react-icons/fa';
import { PRP, PRPLead, PRPMetrics } from '../types/prp';
import '../styles/prp-dashboard-v5.css';

import {
  loadPrpWorkspace,
  prpWorkspaceCommand,
  type PrpWorkspaceReadModel,
} from '../lib/prpWorkspace';

const PrpTerritoryDigitalTwin =
  React.lazy(
    () =>
      import(
        '../components/prp/PrpTerritoryOpportunityExperience'
      ),
  );

const payoutRequestKey=(userId:string)=>`forfeo.prp.payout.request_id.v1:${userId}`;

const stablePayoutRequestId=(userId:string)=>{
  const key=payoutRequestKey(userId);
  const existing=window.sessionStorage.getItem(key);
  if(existing) return existing;
  const next=typeof crypto!=="undefined"&&typeof crypto.randomUUID==="function"
    ? crypto.randomUUID()
    : `prp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem(key,next);
  return next;
};

const csvCell=(value:unknown)=>{
  const raw=value==null?"":String(value);
  return `"${raw.replace(/"/g,'""')}"`;
};

const DASHBOARD_TAB_IDS = [
  "kpi",
  "territory",
  "crm",
  "businesses",
  "wallet",
  "team",
  "settings",
] as const;

type PrpDashboardTab =
  typeof DASHBOARD_TAB_IDS[number];

const isPrpDashboardTab = (
  value: unknown,
): boolean =>
  typeof value === "string"
  && (
    DASHBOARD_TAB_IDS as readonly string[]
  ).includes(value);

export default function PRPDashboard() {
  const navigate = useNavigate();
  const [prpData, setPrpData] = useState<any>(null);
  const [metrics, setMetrics] = useState<PRPMetrics | null>(null);
  const [financialStatement, setFinancialStatement] =
    useState<PrpWorkspaceReadModel["financial_statement"]>(undefined);

  const [territory, setTerritory] =
    useState<PrpWorkspaceReadModel["territory"]>(undefined);
  const [leads, setLeads] = useState<PRPLead[]>([]);
  const [activities, setActivities] = useState<PrpWorkspaceReadModel["activities"]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [payouts, setPayouts] =
    useState<PrpWorkspaceReadModel["payouts"]>([]);

  // ✅ NOUVEAU: États pour l'équipe (Sous-franchise)
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [isAddingMember, setIsAddingMember] =
    usePrpSessionState<boolean>(
      "dashboard.isAddingMember",
      false,
      (value) =>
        typeof value === "boolean",
    );
  const [newMember, setNewMember] = useState({ full_name: '', email: '', commission_rate: 2 });

  const [activeTab, setActiveTab] =
    usePrpSessionState<PrpDashboardTab>(
      "dashboard.activeTab",
      "kpi",
      isPrpDashboardTab,
    );
  const [loading, setLoading] = useState(true);
  const [requestingPayout, setRequestingPayout] = useState(false);

  const [isAddingLead, setIsAddingLead] =
    usePrpSessionState<boolean>(
      "dashboard.isAddingLead",
      false,
      (value) =>
        typeof value === "boolean",
    );
  const [newLead, setNewLead] = useState({ business_name: '', contact_name: '', email: '', phone: '', status: 'prospect', notes: '' });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState("");

  usePrpScrollRestoration(
    "dashboard.scrollY",
    !loading,
  );

  useEffect(() => {
    fetchDashboardData();

    const handleWorkspaceRefresh =
      () => {
        void fetchDashboardData({
          silent: true,
        });
      };
// LOT0F7B_NO_TAB_FOCUS_REFRESH_V1
// A browser tab/window change must never reload PRP workspace data.
window.addEventListener(
      "forfeo:prp-workspace-refresh",
      handleWorkspaceRefresh,
    );
return () => {
      window.removeEventListener(
        "forfeo:prp-workspace-refresh",
        handleWorkspaceRefresh,
      );
};
  }, []);

  useEffect(() => {
    const {
      data: {
        subscription,
      },
    } =
      supabase.auth
        .onAuthStateChange(
          (event) => {
            if (
              event
              === "SIGNED_OUT"
            ) {
              clearPrpUiContinuity();
            }
          },
        );

    return () => {
      subscription
        .unsubscribe();
    };
  }, []);

  const fetchDashboardData = async (
    options: {
      silent?: boolean;
    } = {},
  ) => {
    const silent =
      options.silent
      === true;

    if (!silent) {
      setLoading(true);
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) { navigate('/login'); return; }

      const { data: prp, error: prpError } = await supabase.from('prps').select('*').eq('user_id', userData.user.id).single();
      if (prpError || !prp) {
        setPrpData(null);
        if (!silent) {
          setLoading(false);
        }
        return;
      }

      setPrpData(prp);
      setStripeAccountId(prp.stripe_account_id || "");

      if (prp.status === 'pending') {
        if (!silent) {
          setLoading(false);
        }
        return;
      }

      const workspace =
        await loadPrpWorkspace();

      if (
        workspace.profile?.id
        !== prp.id
      ) {
        throw new Error(
          "prp_workspace_identity_mismatch",
        );
      }

      setMetrics(
        workspace.metrics as PRPMetrics,
      );

      setPayouts(
        workspace.payouts
        || [],
      );

      setFinancialStatement(
        workspace.financial_statement,
      );

      setTerritory(
        workspace.territory,
      );

      setLeads(
        (
          workspace.leads
          || []
        ) as PRPLead[],
      );

      setActivities(
        workspace.activities
        || [],
      );

      setBusinesses(
        workspace.merchants
        || [],
      );

      setTeamMembers(
        workspace.team
        || [],
      );

    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // --- ACTIONS FINANCES ---
  const handleRequestPayout = async () => {
    if(requestingPayout) return;
    if(!window.confirm(
      "Confirmez-vous la demande de virement ? Giftshop déterminera le montant et les lignes admissibles côté serveur."
    )) return;

    setRequestingPayout(true);
    try {
      const {data:{session}}=await supabase.auth.getSession();
      if(!session?.access_token||!session.user?.id) throw new Error("Session absente");
      const requestId=stablePayoutRequestId(session.user.id);
      const response=await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-payout`,
        {
          method:"POST",
          headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
          body:JSON.stringify({payout_type:"prp",request_id:requestId}),
        },
      );
      const result=await response.json();
      if(!response.ok||result?.success!==true||!result?.payout){
        throw new Error(result?.error||"Erreur lors de la demande");
      }
      window.sessionStorage.removeItem(payoutRequestKey(session.user.id));
      const cents=Number(result.payout.total_cents);
      const currency=String(result.payout.currency||"").toUpperCase();
      const label=Number.isFinite(cents)&&currency?`${(cents/100).toFixed(2)} ${currency}`:"montant déterminé par Giftshop";
      alert(`✅ Demande créée : ${label}.`);
      await fetchDashboardData({ silent: true });
    } catch(err:any) {
      alert("Erreur : "+(err?.message||"Demande impossible"));
    } finally {
      setRequestingPayout(false);
    }
  };

  // --- ACTIONS CRM ---
  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newLead.business_name || !newLead.contact_name) {
      return alert("Infos requises.");
    }

    try {
      await prpWorkspaceCommand(
        "lead_create",
        {
          business_name: newLead.business_name,
          contact_name: newLead.contact_name,
          email: newLead.email || null,
          phone: newLead.phone || null,
          status: newLead.status || "prospect",
          notes: newLead.notes || null,
        },
      );

      setIsAddingLead(false);

      setNewLead({
        business_name: '',
        contact_name: '',
        email: '',
        phone: '',
        status: 'prospect',
        notes: '',
      });

      await fetchDashboardData({ silent: true });
    } catch (err: any) {
      alert(
        "Erreur ajout: "
        + (err?.message || "Commande impossible")
      );
    }
  };

  const handleUpdateLeadStatus = async (
    id: string,
    newStatus: string,
  ) => {
    try {
      await prpWorkspaceCommand(
        "lead_update_status",
        {
          lead_id: id,
          status: newStatus,
        },
      );

      await fetchDashboardData({ silent: true });
    } catch (err: any) {
      alert(
        "Erreur : "
        + (err?.message || "Mise à jour impossible")
      );
    }
  };

  const handleDeleteLead = async (
    id: string,
  ) => {
    if (
      !confirm(
        "Archiver ce prospect ? Son historique sera conservé."
      )
    ) {
      return;
    }

    try {
      await prpWorkspaceCommand(
        "lead_archive",
        {
          lead_id: id,
        },
      );

      await fetchDashboardData({ silent: true });
    } catch (err: any) {
      alert(
        "Erreur : "
        + (err?.message || "Archivage impossible")
      );
    }
  };

  // --- ACTIONS ÉQUIPE — serveur uniquement ---
  const handleAddTeamMember = async (
    e: React.FormEvent,
  ) => {
    e.preventDefault();

    try {
      await prpWorkspaceCommand(
        "team_add",
        {
          full_name: newMember.full_name,
          email: newMember.email,
          commission_rate: newMember.commission_rate,
        },
      );

      setIsAddingMember(false);

      setNewMember({
        full_name: '',
        email: '',
        commission_rate: 2,
      });

      await fetchDashboardData({ silent: true });

      alert(
        "✅ Recruteur ajouté. "
        + "Sa rémunération historique reste en attente "
        + "du moteur financier officiel."
      );
    } catch (err: any) {
      alert(
        "Erreur ajout recruteur: "
        + (err?.message || "Commande impossible")
      );
    }
  };

  const handleDeleteTeamMember = async (
    id: string,
  ) => {
    if (
      !confirm(
        "Retirer ce recruteur de votre équipe ? "
        + "Son historique sera conservé."
      )
    ) {
      return;
    }

    try {
      await prpWorkspaceCommand(
        "team_remove",
        {
          member_id: id,
        },
      );

      await fetchDashboardData({ silent: true });
    } catch (err: any) {
      alert(
        "Erreur : "
        + (err?.message || "Retrait impossible")
      );
    }
  };

  const copyRecruiterLink = (recruiterId: string) => {
    if (!prpData) return;
    // Le lien inclut l'ID du PRP (pour que la boîte soit affiliée) ET l'ID du Recruteur (pour diviser l'argent)
    const link = `${window.location.origin}/login?prp=${prpData.id}&closer=${recruiterId}`;
    navigator.clipboard.writeText(link);
    alert("✅ Lien du recruteur copié ! Envoyez-le lui.");
  };

  // --- RESTE ---
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault(); setPasswordMessage(null);
    if (newPassword !== confirmPassword) return setPasswordMessage({ type: 'error', text: "Mots de passe différents." });
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) setPasswordMessage({ type: 'error', text: error.message }); else { setPasswordMessage({ type: 'success', text: "Mis à jour !" }); setNewPassword(""); setConfirmPassword(""); }
  };

  const handleUpdateStripe = async (e: React.FormEvent) => {
    e.preventDefault();

    alert(
      "La connexion bancaire PRP ne peut plus être enregistrée "
      + "directement depuis le navigateur. "
      + "Le nouveau flux de paiement sécurisé sera finalisé "
      + "avec le ledger Giftshop."
    );

    if (error) alert("Erreur : " + error.message); else alert("✅ Compte Stripe enregistré !");
  };

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/'); };

  // FORFEO_PRP_V71_EXTERNAL_SHELL_ACTION_DEDUP
  useEffect(() => {
    const markExternalShellActions = () => {
      const candidates = Array.from(
        document.querySelectorAll<HTMLAnchorElement | HTMLButtonElement>('a, button'),
      );

      for (const element of candidates) {
        if (element.closest('[data-prp-v7-layout="true"]')) continue;

        const label = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (label === 'retour' || label === 'accueil') {
          element.setAttribute('data-prp-v71-external-shell-action', 'true');
        }
      }
    };

    markExternalShellActions();
    const observer = new MutationObserver(markExternalShellActions);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document
        .querySelectorAll('[data-prp-v71-external-shell-action="true"]')
        .forEach((element) => element.removeAttribute('data-prp-v71-external-shell-action'));
    };
  }, []);
  const exportCSV = () => {
    if(financialStatement?.financial_authority!=="giftshop"||financialStatement?.ledger_source!=="prp_commission_ledger") return alert("Relevé canonique Giftshop indisponible.");
    const rows=Array.isArray(financialStatement.ledger_rows)?financialStatement.ledger_rows:[];
    const header=["source_event_key","order_id","order_line_id","merchant_id","commercial_policy_code","currency","eligible_component_gross_cents","platform_fee_cents","prp_share_cents","reversed_prp_share_cents","status","payout_reference","source_processor_reference","payout_processor_reference","created_at","updated_at"];
    const lines=[header.map(csvCell).join(","),...rows.map(row=>header.map(field=>csvCell(row?.[field])).join(","))];
    const rawDate=String(financialStatement.generated_at||"").slice(0,10);
    const safeDate=/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(rawDate)?rawDate:"statement";
    const url=URL.createObjectURL(new Blob(["\uFEFF"+lines.join("\r\n")+"\r\n"],{type:"text/csv;charset=utf-8"}));
    const link=document.createElement("a");
    link.href=url; link.download=`forfeo-prp-statement-${safeDate}.csv`;
    document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
  };

  const copyAffiliateLink = () => { if (!prpData) return; navigator.clipboard.writeText(`${window.location.origin}/login?prp=${prpData.id}`); alert("✅ Lien copié !"); };

  const toNum = (val: any) => { const n = Number(val); return isNaN(n) ? 0 : n; };
  const monthlyGoal = 10000; const currentRevenue = toNum(metrics?.monthly_subscription_revenue); const progressPercent = Math.min((currentRevenue / monthlyGoal) * 100, 100);
  const canonicalFinanceReady=financialStatement?.financial_authority==="giftshop"&&financialStatement?.ledger_source==="prp_commission_ledger";
  const canonicalLedgerRowCount=Array.isArray(financialStatement?.ledger_rows)?financialStatement.ledger_rows.length:0;
  const canonicalRecoveryCount=Array.isArray(financialStatement?.recoveries)?financialStatement.recoveries.length:0;
  const canonicalPayoutPending=payouts.some(p=>["requested","approved"].includes(String(p.canonical_status||p.status||"").toLowerCase()));
  const payoutAmount=(p:PrpWorkspaceReadModel["payouts"][number])=>{
    const cents=Number(p.total_cents); const currency=String(p.currency||"").toUpperCase();
    return Number.isFinite(cents)&&currency?`${(cents/100).toFixed(2)} ${currency}`:"—";
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="text-center"><FaSync className="animate-spin text-indigo-500 text-4xl mx-auto mb-4" /><p className="text-indigo-200 font-black">Chargement...</p></div></div>;
  if (!prpData) return <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4"><div className="bg-white/10 p-5 md:p-10 rounded-3xl text-center"><FaExclamationTriangle className="text-6xl text-red-500 mx-auto mb-4" /><h2 className="text-3xl font-black text-white mb-4">Accès refusé</h2><button onClick={handleLogout} className="w-full bg-white text-slate-900 py-4 rounded-xl font-black uppercase hover:bg-slate-200">Se déconnecter</button></div></div>;
  if (prpData?.status === 'pending') return <div className="min-h-screen bg-slate-900 flex flex-col font-sans text-white"><Header /><div className="flex-1 flex items-center justify-center p-4"><div className="bg-white/10 p-5 md:p-10 rounded-[2.5rem] max-w-lg text-center"><FaClock className="text-6xl text-amber-400 mx-auto mb-6" /><h2 className="text-3xl font-black mb-4">Compte en attente</h2><p className="mb-8">L'équipe Forfeo doit approuver votre compte.</p><button onClick={handleLogout} className="w-full bg-white text-slate-900 py-4 rounded-2xl font-black uppercase">Se déconnecter</button></div></div></div>;

  return (
    <div data-prp-v7-layout="true" className="prp-v5-shell prp-pixel-shell-v4r2 min-h-screen bg-slate-50 font-sans text-slate-900 pb-20">
      <div className="prp-v5-hero prp-pixel-hero-v4r2 bg-white text-slate-900 pt-8 pb-8 px-4 relative overflow-hidden">
        <div className="absolute top-[-50%] left-[-10%] w-96 h-96 bg-indigo-600 rounded-full mix-blend-screen filter blur-[128px] opacity-40 animate-pulse"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-96 h-96 bg-purple-600 rounded-full mix-blend-screen filter blur-[128px] opacity-30"></div>
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2"><h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-400">Espace PRP</h1><span className="bg-white/10 border border-white/20 px-3 py-1 rounded-full text-xs font-bold text-indigo-300 flex items-center gap-1"><FaMapPin size={12} /> {prpData?.region}</span></div>
              <p className="text-slate-400 font-medium max-w-xl">Organisez votre territoire, développez vos relations et accompagnez le réseau Forfeo.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={copyAffiliateLink} className="bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-2.5 rounded-xl font-bold transition flex items-center gap-2 text-sm"><FaLink /> Lien d'invitation personnel</button>
              <button onClick={exportCSV} className="bg-indigo-600 hover:bg-indigo-500 px-5 py-2.5 rounded-xl font-bold transition flex items-center gap-2 text-sm shadow-lg"><FaDownload /> Exporter CSV</button>
            </div>
          </div>
          <div className="mt-10 bg-white/5 border border-white/10 p-6 rounded-[2rem] backdrop-blur-xl">
            <div className="flex justify-between items-end mb-3">
              <div><span className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1 block">Objectif mensuel</span><span className="text-2xl font-black text-white">{currentRevenue.toFixed(2)}$ <span className="text-sm text-slate-500">/ {monthlyGoal}$</span></span></div>
              <FaMedal className={`text-3xl ${progressPercent === 100 ? "text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.5)]" : "text-slate-600"}`} />
            </div>
            <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden shadow-inner"><div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${progressPercent}%` }}></div></div>
          </div>
        </div>
      </div>

      <div className="prp-v5-content prp-pixel-content-v4r2 container mx-auto max-w-7xl px-4 -mt-2 relative z-20">
        <div data-prp-v7-sidebar="true" className="prp-v5-rail prp-pixel-rail-v4r2 bg-white/80 backdrop-blur-xl border border-slate-200 p-2 rounded-2xl flex gap-2 overflow-x-auto shadow-xl mb-8 custom-scrollbar">
          <div className="prp-v7-brand" aria-label="Forfeo Explore — Espace PRP">
            <span className="prp-v7-brand-mark" aria-hidden="true"><span className="prp-v7-brand-needle" /></span>
            <span className="prp-v7-brand-copy">
              <span className="prp-v7-brand-line"><strong>FORFEO</strong><em>EXPLORE</em></span>
              <span className="prp-v7-brand-subtitle">ESPACE PRP</span>
            </span>
          </div>
          <div data-prp-v71-nav-scroll="true" className="prp-v71-nav-scroll">
            {[{ id: 'kpi', label: 'Vue d\'ensemble', icon: <FaChartLine /> }, { id: 'territory', label: 'Territoire', icon: <FaMapPin /> }, { id: 'crm', label: 'Pipeline CRM', icon: <FaUsers /> }, { id: 'businesses', label: 'Mes Partenaires', icon: <FaBuilding /> }, { id: 'wallet', label: 'Finances', icon: <FaMoneyBillWave /> }, { id: 'team', label: 'Mon Équipe', icon: <FaUsers /> }, { id: 'settings', label: 'Paramètres', icon: <FaLock /> }].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id as PrpDashboardTab)} className={`prp-v5-nav-btn flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}>{tab.icon} {tab.label}</button>
            ))}
          </div>
          <div data-prp-v71-footer="true" className="prp-v71-sidebar-footer">
            <div className="prp-v71-footer-nav">
              <button type="button" onClick={() => navigate(-1)} className="prp-v71-footer-action" aria-label="Retour">
                <FaArrowLeft />
                <span>Retour</span>
              </button>
              <button type="button" onClick={() => navigate('/')} className="prp-v71-footer-action" aria-label="Accueil">
                <FaHome />
                <span>Accueil</span>
              </button>
            </div>
            <button
              type="button"
              data-prp-v7-logout="true"
              onClick={handleLogout}
              className="prp-v7-logout"
              aria-label="Déconnexion"
            >
              <FaSignOutAlt />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>

        {activeTab === 'kpi' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100"><div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-xl mb-4"><FaBuilding /></div><p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Partenaires Actifs</p><h3 className="text-3xl font-black text-slate-900">{metrics?.active_partners || 0} <span className="text-lg text-slate-400 font-medium">/ {metrics?.total_partners || 0}</span></h3></div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100"><div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center text-xl mb-4"><FaMoneyBillWave /></div><p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Volume d'Affaires</p><h3 className="text-3xl font-black text-slate-900">{toNum(metrics?.monthly_subscription_revenue).toFixed(2)}$</h3></div>
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-6 rounded-3xl shadow-lg border border-indigo-400 text-white"><div className="w-12 h-12 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center text-xl mb-4"><FaChartLine /></div><p className="text-xs font-black text-indigo-200 uppercase tracking-widest mb-1">Écritures finance</p><h3 className="text-3xl font-black">{canonicalLedgerRowCount}</h3><p className="text-[10px] font-bold mt-2 bg-white/10 w-fit px-2 py-1 rounded-lg">Autorité : Giftshop</p></div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 cursor-pointer" onClick={() => setActiveTab('crm')}><div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center text-xl mb-4"><FaUsers /></div><p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Leads en cours</p><h3 className="text-3xl font-black text-slate-900">{leads.filter(l => l.status !== 'signed').length}</h3></div>
          </div>
        )}

        {/* ✅ NOUVEAU: ONGLET MON ÉQUIPE */}
        {activeTab === 'territory' && (
          <React.Suspense
            fallback={
              <div className="min-h-[540px] rounded-[2rem] border border-slate-200 bg-slate-950 flex items-center justify-center text-white">
                <div className="text-center">
                  <FaSync className="mx-auto mb-4 animate-spin text-2xl text-cyan-300" />
                  <p className="font-black">
                    Chargement du territoire…
                  </p>
                </div>
              </div>
            }
          >
            <PrpTerritoryDigitalTwin
              territory={territory}
              leads={
                leads as PrpWorkspaceReadModel["leads"]
              }
              merchants={
                businesses as PrpWorkspaceReadModel["merchants"]
              }
            />
          </React.Suspense>
        )}

        {activeTab === 'team' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex justify-between items-center bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
              <div>
                <h3 className="text-xl font-black text-slate-900">Mon équipe</h3>
                <p className="text-sm text-slate-500">Ajoutez des collaborateurs autorisés pour soutenir le développement relationnel de votre territoire.</p>
              </div>
              <button onClick={() => setIsAddingMember(!isAddingMember)} className="bg-slate-900 text-white px-5 py-3 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-slate-800 transition shadow-lg">
                {isAddingMember ? <FaTimes /> : <FaPlus />} {isAddingMember ? 'Fermer' : 'Ajouter un collaborateur'}
              </button>
            </div>

            {isAddingMember && (
              <form onSubmit={handleAddTeamMember} className="bg-indigo-50 border-2 border-indigo-100 p-6 rounded-3xl grid grid-cols-1 md:grid-cols-4 gap-4 animate-in zoom-in-95 duration-200">
                <input required type="text" placeholder="Nom complet" className="p-3 rounded-xl border border-indigo-200 font-bold text-sm outline-none" value={newMember.full_name} onChange={e => setNewMember({...newMember, full_name: e.target.value})} />
                <input required type="email" placeholder="Courriel du collaborateur" className="p-3 rounded-xl border border-indigo-200 font-medium text-sm outline-none" value={newMember.email} onChange={e => setNewMember({...newMember, email: e.target.value})} />
                <div className="relative">
                  <span className="absolute -top-2 left-2 bg-indigo-50 px-1 text-[10px] font-black text-indigo-600 uppercase tracking-widest">Taux collaborateur (%)</span>
                  <input type="number" step="0.5" max={(prpData?.commission_rate * 100) - 0.5} required className="w-full p-3 rounded-xl border border-indigo-200 font-bold text-sm outline-none" value={newMember.commission_rate} onChange={e => setNewMember({...newMember, commission_rate: Number(e.target.value)})} />
                </div>
                <button type="submit" className="bg-indigo-600 text-white font-black uppercase tracking-widest text-xs rounded-xl hover:bg-indigo-700 transition shadow-md">Ajouter</button>
                <div className="md:col-span-4 text-xs font-bold text-indigo-500">
                  ⚠️ Le collaborateur reçoit {newMember.commission_rate}% sur la vente. Répartition restante selon les paramètres autorisés : {((prpData?.commission_rate * 100) - newMember.commission_rate).toFixed(1)}%.
                </div>
              </form>
            )}

            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <div className="min-w-[760px]">
                  <table className="w-full text-left">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                  <tr><th className="p-5 text-xs font-black uppercase">Recruteur</th><th className="p-5 text-xs font-black uppercase">Commission Partagée</th><th className="p-5 text-xs font-black uppercase">Lien Unique</th><th className="p-5 text-right text-xs font-black uppercase">Action</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {teamMembers.map(member => (
                    <tr key={member.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-5">
                        <div className="font-black text-slate-900 text-base">{member.full_name}</div>
                        <div className="text-sm text-slate-500">{member.email}</div>
                      </td>
                      <td className="p-5">
                        <div className="flex items-center gap-2">
                          <span className="bg-purple-100 text-purple-700 font-black px-3 py-1 rounded-lg text-sm">{Number(member.commission_rate * 100).toFixed(1)}% <span className="text-[10px] uppercase">Lui</span></span>
                          <span className="bg-indigo-100 text-indigo-700 font-black px-3 py-1 rounded-lg text-sm">{((prpData?.commission_rate * 100) - (member.commission_rate * 100)).toFixed(1)}% <span className="text-[10px] uppercase">Vous</span></span>
                        </div>
                      </td>
                      <td className="p-5">
                        <button onClick={() => copyRecruiterLink(member.id)} className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-xl text-xs font-black uppercase transition">
                          <FaCopy /> Copier son lien
                        </button>
                      </td>
                      <td className="p-5 text-right">
                        <button onClick={() => handleDeleteTeamMember(member.id)} className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition"><FaTrash /></button>
                      </td>
                    </tr>
                  ))}
                  {teamMembers.length === 0 && <tr><td colSpan={4} className="p-12 text-center text-slate-400 font-bold">Votre équipe est vide. Ajoutez un recruteur pour commencer à déléguer.</td></tr>}
                </tbody>
              </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ... RESTE INCHANGÉ ... */}

        {activeTab === 'wallet' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="bg-slate-950 text-white p-8 rounded-3xl shadow-xl border border-slate-800">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-300">Finance Wallet canonique</p>
              <h3 className="text-3xl font-black mt-2">Giftshop décide. Explore affiche.</h3>
              <p className="text-sm text-slate-300 mt-3">Aucun solde, minimum, montant ou ligne de payout n'est choisi dans le navigateur.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                <div className="bg-white/5 rounded-2xl p-4"><p className="text-xs text-slate-400">Ledger</p><p className="font-black">prp_commission_ledger</p></div>
                <div className="bg-white/5 rounded-2xl p-4"><p className="text-xs text-slate-400">Écritures</p><p className="text-3xl font-black">{canonicalLedgerRowCount}</p></div>
                <div className="bg-white/5 rounded-2xl p-4"><p className="text-xs text-slate-400">Recoveries</p><p className="text-3xl font-black">{canonicalRecoveryCount}</p></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button onClick={handleRequestPayout} disabled={requestingPayout||canonicalPayoutPending||!canonicalFinanceReady} className="flex-1 bg-white text-slate-950 py-4 rounded-2xl font-black uppercase disabled:opacity-50">{requestingPayout?<FaSync className="animate-spin inline mr-2"/>:<FaMoneyBillWave className="inline mr-2"/>}{canonicalPayoutPending?'Demande en cours':'Demander un virement'}</button>
                <button onClick={exportCSV} disabled={!canonicalFinanceReady} className="bg-indigo-600 text-white px-6 py-4 rounded-2xl font-black uppercase disabled:opacity-50"><FaDownload className="inline mr-2"/>Relevé CSV</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{['earned','payable','requested','approved','settled','reversed','recovery_required'].map(status=><div key={status} className="bg-white border border-slate-100 rounded-2xl p-4"><p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{status}</p></div>)}</div>
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-6 border-b border-slate-100"><h3 className="font-black">Historique canonique des virements</h3></div>
              {payouts.length===0?<div className="p-8 text-center text-slate-500">Aucun payout canonique.</div>:<div className="overflow-x-auto"><table className="w-full min-w-[680px] text-left"><thead className="bg-slate-50"><tr><th className="p-4">Date</th><th className="p-4">Montant Giftshop</th><th className="p-4">Lignes</th><th className="p-4">Statut</th></tr></thead><tbody>{payouts.map(pay=><tr key={pay.id||`${pay.created_at}-${pay.total_cents}`} className="border-t border-slate-50"><td className="p-4">{pay.created_at?new Date(pay.created_at).toLocaleDateString('fr-CA'):'—'}</td><td className="p-4 font-black">{payoutAmount(pay)}</td><td className="p-4">{typeof pay.line_count==='number'?pay.line_count:'—'}</td><td className="p-4 uppercase text-xs font-black">{String(pay.canonical_status||pay.status||'unknown')}</td></tr>)}</tbody></table></div>}
            </div>
          </div>
        )}

        {activeTab === 'crm' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <PrpDemandRadarGapMap
              leads={leads as any[]}
              activities={activities as any[]}
              onRefresh={() => fetchDashboardData({ silent: true })}
            />
            <PrpPipelineTerrainCockpit
              leads={leads as any[]}
              activities={activities as any[]}
              onRefresh={() => fetchDashboardData({ silent: true })}
            />
            <PrpTourneeTerrainIntelligente
              leads={leads as any[]}
              activities={activities as any[]}
              onRefresh={() => fetchDashboardData({ silent: true })}
            />
            <PrpPartnerConversionCockpit
              leads={leads as any[]}
              activities={activities as any[]}
              onRefresh={() => fetchDashboardData({ silent: true })}
            />

            <div className="flex justify-between items-center bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
              <div><h3 className="text-xl font-black text-slate-900">Pipeline de Prospection — vue classique</h3></div>
              <button onClick={() => setIsAddingLead(!isAddingLead)} className="bg-slate-900 text-white px-5 py-3 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-slate-800 transition shadow-lg">{isAddingLead ? <FaTimes /> : <FaPlus />} {isAddingLead ? 'Fermer' : 'Nouveau Lead'}</button>
            </div>
            {isAddingLead && (
              <form onSubmit={handleAddLead} className="bg-indigo-50 border-2 border-indigo-100 p-6 rounded-3xl grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 animate-in zoom-in-95">
                <input required type="text" placeholder="Entreprise" className="p-3 rounded-xl border border-indigo-200 font-bold text-sm" value={newLead.business_name} onChange={e => setNewLead({...newLead, business_name: e.target.value})} />
                <input required type="text" placeholder="Contact" className="p-3 rounded-xl border border-indigo-200 font-medium text-sm" value={newLead.contact_name} onChange={e => setNewLead({...newLead, contact_name: e.target.value})} />
                <input type="email" placeholder="Email" className="p-3 rounded-xl border border-indigo-200 font-medium text-sm" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} />
                <input type="text" placeholder="Téléphone" className="p-3 rounded-xl border border-indigo-200 font-medium text-sm" value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} />
                <button type="submit" className="bg-indigo-600 text-white font-black uppercase text-xs rounded-xl hover:bg-indigo-700 transition">Enregistrer</button>
              </form>
            )}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <div className="min-w-[760px]">
                  <table className="w-full text-left">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-100"><tr><th className="p-5 text-xs font-black uppercase">Prospect</th><th className="p-5 text-xs font-black uppercase">Coordonnées</th><th className="p-5 text-xs font-black uppercase">Statut</th><th className="p-5 text-right text-xs font-black uppercase">Actions</th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {leads.map(lead => (
                    <tr key={lead.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-5"><div className="font-black text-slate-900 text-base">{lead.business_name}</div><div className="text-sm text-slate-500">{lead.contact_name}</div></td>
                      <td className="p-5 text-sm font-medium text-slate-600 space-y-1">{lead.email && <div className="flex items-center gap-2"><FaEnvelope className="text-slate-300"/> {lead.email}</div>}{lead.phone && <div className="text-xs text-slate-400">{lead.phone}</div>}</td>
                      <td className="p-5"><select className="text-xs font-black uppercase p-2 rounded-xl border-2" value={lead.status} onChange={(e) => handleUpdateLeadStatus(lead.id, e.target.value)}><option value="prospect">Prospect</option><option value="meeting">Rendez-vous</option><option value="contract_sent">Contrat Envoyé</option><option value="signed">Signé (Gagné)</option></select></td>
                      <td className="p-5 text-right"><button onClick={() => handleDeleteLead(lead.id)} className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-100"><FaTrash /></button></td>
                    </tr>
                  ))}
                  {leads.length === 0 && <tr><td colSpan={4} className="p-12 text-center text-slate-400 font-bold">Pipeline vide.</td></tr>}
                </tbody>
              </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'businesses' && (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden animate-in fade-in slide-in-from-bottom-4">
             <div className="p-6 border-b border-slate-100 bg-slate-50/50"><h3 className="text-xl font-black text-slate-900">Vos Partenaires Onboardés</h3></div>
             <div className="overflow-x-auto">
               <div className="min-w-[640px]">
                 <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100"><tr><th className="p-5 text-xs font-black uppercase">Entreprise</th><th className="p-5 text-xs font-black uppercase">Date d'inscription</th><th className="p-5 text-xs font-black uppercase">Statut</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {businesses.map(biz => (
                  <tr key={biz.id} className="hover:bg-slate-50 transition">
                    <td className="p-5"><div className="font-black text-slate-900 text-lg">{biz.name}</div><div className="text-xs text-slate-500">{biz.email}</div></td>
                    <td className="p-5 text-sm font-medium text-slate-600">{new Date(biz.created_at).toLocaleDateString()}</td>
                    <td className="p-5">{biz.is_approved ? <span className="bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-[10px] font-black uppercase flex items-center gap-1 w-fit"><FaCheckCircle /> Validé</span> : <span className="bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-[10px] font-black uppercase flex items-center gap-1 w-fit">En attente</span>}</td>
                  </tr>
                ))}
                {businesses.length === 0 && <tr><td colSpan={3} className="p-12 text-center text-slate-400 font-bold">Aucun partenaire signé.</td></tr>}
              </tbody>
            </table>
               </div>
             </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
              <h3 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-3"><FaMoneyBillWave className="text-green-600" /> Compte de versement</h3>
              <p className="text-sm text-slate-500 mb-6">Entrez votre ID de compte Stripe Connect (ex: acct_...) pour recevoir vos revenus automatiquement.</p>
              <form onSubmit={handleUpdateStripe} className="flex flex-col sm:flex-row gap-4">
                <input type="text" value={stripeAccountId} onChange={(e) => setStripeAccountId(e.target.value)} className="flex-1 p-4 bg-slate-50 rounded-2xl border-2 border-slate-100 font-bold outline-none focus:border-green-500" placeholder="ex: acct_123456789" required />
                <button type="submit" className="bg-green-600 text-white px-8 py-4 rounded-2xl font-black uppercase hover:bg-green-700 transition shadow-lg">Sauvegarder</button>
              </form>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div><h3 className="text-xl font-black text-slate-900 flex items-center gap-3"><FaLock className="text-red-500" /> Sécurité</h3></div>
                <button onClick={handleLogout} className="bg-red-50 text-red-500 p-3 rounded-xl hover:bg-red-100 transition"><FaSignOutAlt size={20} /></button>
              </div>
              {passwordMessage && <div className="p-4 rounded-xl mb-6 text-sm font-bold bg-slate-100">{passwordMessage.text}</div>}
              <form onSubmit={handleUpdatePassword} className="space-y-5">
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-slate-100 font-bold" placeholder="Nouveau mot de passe" required />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-slate-100 font-bold" placeholder="Confirmer le mot de passe" required />
                <button type="submit" className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase hover:bg-slate-800 transition shadow-lg">Mettre à jour</button>
              </form>
            </div>
          </div>
        )}
      </div>
      <div className="prp-v6-extras">
        <PrpNearbyScoutPanel />
        <div className="prp-v5-forfy">
          <PrpForfyAgentsPanel />
        </div>
      </div>

    </div>
  );
}
